import { c } from 'ttag';

import { MembershipDisplayStatus } from '../../graphql/__generated__/schema';
import { PortalUserQuery } from '../../graphql/__generated__/types';
import { formatDate } from './display';
import { statusLabel } from './status';

type Membership = NonNullable<NonNullable<PortalUserQuery['portalUser']>['memberships']>[number];

/**
 * How the last closure of a membership is read, and the reading is the whole point
 * of this file.
 *
 * **Attention, `revokedAt`, `revokedReason` and `revokedBy` describe the LAST closure, never
 * the current state.** Reopening does not clear them, so they stay filled on
 * memberships that are alive today: a person who was closed and reopened carries a
 * reason **and** an open access at the same time, and that is normal. A screen that
 * prints them without reading `status` first announces a closure that has been
 * undone, which is how an operator ends up reopening what is already open.
 *
 * So `status` is read first, always, and the three fields only ever reach the screen
 * through one of the states below.
 */
export type ClosureState =
    /** No closure was ever recorded on this membership. */
    | { kind: 'none' }
    /** The access is closed **now**, and this is why. The reopening gesture applies. */
    | { kind: 'current'; at: string | null; reason: string | null; by: string | null }
    /**
     * A closure happened and is no longer what the access carries: reopened since,
     * or overtaken by another state. History, not a situation to act on.
     */
    | { kind: 'past'; at: string | null; reason: string | null; by: string | null };

export const closureStateOf = (membership: Membership): ClosureState => {
    const facts = {
        at: membership.revokedAt ?? null,
        reason: membership.revokedReason ?? null,
        by: membership.revokedBy ?? null,
    };
    // `status` first: it is the only one of the four fields that describes today.
    if (membership.status === MembershipDisplayStatus.AccessRevoked) {
        return { kind: 'current', ...facts };
    }
    if (!facts.at) {
        return { kind: 'none' };
    }
    return { kind: 'past', ...facts };
};

/**
 * Who closed it and when.
 *
 * Both halves go missing for different reasons: the author is null once their own
 * account has been deleted: the foreign key is `ON DELETE SET NULL`, so a closure
 * outlives its author, and « par un auteur inconnu » would be the wrong words for
 * that, nobody being unknown.
 */
export const closureAuthorSentence = (state: ClosureState): string | null => {
    if (state.kind === 'none') {
        return null;
    }
    const date = formatDate(state.at);
    if (state.by) {
        const author = state.by;
        return c('Membership closure').t`Closed on ${date} by ${author}.`;
    }
    return c('Membership closure').t`Closed on ${date}. The account that decided it no longer exists.`;
};

/**
 * The motive, or the sentence that says a motive was never given.
 *
 * Attention, A null reason covers two honest cases: the closure stated no motive, the
 * argument of `revokeMembership` being optional, or nothing was ever recorded: and
 * saying so is what has to be shown, not a blank. A blank reads as a field that
 * failed to load, and sends the operator looking for a reason nobody ever wrote.
 */
export const closureReasonSentence = (state: ClosureState): string | null => {
    if (state.kind === 'none') {
        return null;
    }
    return state.reason ?? c('Membership closure').t`No reason was recorded for this closure.`;
};

/**
 * What a **past** closure has to carry so it is not mistaken for the situation.
 *
 * Attention, it says what the access reads **today** rather than claiming the closure was
 * undone: on a disabled person the display status masks the stored one, so « rouvert »
 * would sometimes be a guess. The status is a fact.
 */
export const closureIsHistorySentence = (state: ClosureState, membership: Membership): string | null => {
    if (state.kind !== 'past') {
        return null;
    }
    if (!membership.status) {
        return c('Membership closure').t`That is a past event, not the current state of this access.`;
    }
    const current = statusLabel(membership.status);
    return c('Membership closure').t`That is a past event: this access now reads « ${current} ».`;
};

/**
 * Whether the sheet offers to reopen this membership, and why the control is dead
 * when it is.
 *
 * **Attention, the closed access is what carries the gesture.** A disabled row is the one
 * case the payload cannot decide: `DISABLED` outranks the stored status in the
 * `membership_status` view, so a membership of a disabled person may or may not also
 * be closed, and the API answers `ACCOUNT_DISABLED` either way. The control is
 * therefore offered and disabled with that reason on such a row when it also carries
 * a closure: removed, the gesture would look as though it existed on one sheet and
 * not on another.
 *
 * Attention, the other refusals cannot be pre-empted and are rendered after the click:
 * whether the account is still open to the portal is not in this query, and whether
 * the actor may grant the role the membership resumes needs the actor's permissions,
 * which `viewer` does not expose.
 */
export interface ReinstateOffer {
    /** Whether the control is rendered at all. */
    offered: boolean;
    /** Non-null when it is rendered dead, with this as its reason. */
    blocker: string | null;
}

export const reinstateOfferOf = (membership: Membership, state: ClosureState): ReinstateOffer => {
    if (state.kind === 'current') {
        return { offered: true, blocker: null };
    }
    if (state.kind === 'past' && membership.status === MembershipDisplayStatus.Disabled) {
        return {
            offered: true,
            blocker: c('Membership closure')
                .t`This person's account is disabled, which masks the state of each of their accesses. Reactivate the account first: reopening would produce an access the portal keeps refusing.`,
        };
    }
    return { offered: false, blocker: null };
};
